import {
  Portfolio,
  Project,
  Contract,
  TokenData,
  LoginResult,
  ArtifactsToProjectSightRequest,
  ArtifactsToProjectSightResponse,
  ArtifactsToProjectSightError,
} from '@/integrations/trimble/types'
import TrimbleAuthManager from '@/integrations/trimble/trimbleAuthManager'

// Singleton instance of TrimbleAuthManager
let trimbleAuthManagerInstance: TrimbleAuthManager | null = null

/**
 * Get or create the TrimbleAuthManager instance
 * @param baseUrl Base URL for Trimble API requests (optional)
 */
export function getTrimbleAuthManager(baseUrl?: string): TrimbleAuthManager {
  if (!trimbleAuthManagerInstance) {
    trimbleAuthManagerInstance = new TrimbleAuthManager(baseUrl || '')
  }
  return trimbleAuthManagerInstance
}

/**
 * Make an authenticated API call to the Trimble API
 * @param endpoint API endpoint to call
 * @param options Fetch options
 * @returns Promise with the response
 */
export async function makeApiCall(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  try {
    const manager = getTrimbleAuthManager()
    return await manager.apiCall(endpoint, options)
  } catch (error) {
    console.error('API call error:', error)
    throw error
  }
}

/**
 * Get user information directly from the Trimble API
 * This is used to validate the Trimble session
 */
export async function getUserInfoDirect(): Promise<TokenData | null> {
  try {
    const manager = getTrimbleAuthManager()
    return await manager.getUserInfoDirect()
  } catch (error) {
    console.error('Error getting user info:', error)
    return null
  }
}

/**
 * Initiate OAuth login with Trimble
 * @param usePopup Whether to use a popup window for login (default: true)
 * @returns Promise with the login result
 */
export async function login(
  usePopup = true
): Promise<boolean | void | LoginResult> {
  try {
    const manager = getTrimbleAuthManager()
    return await manager.login(usePopup)
  } catch (error) {
    console.error('Error logging in:', error)
    throw error
  }
}

/**
 * Get stored token from localStorage
 * @returns The stored token or null if not found
 */
export function getStoredToken(): TokenData | null {
  try {
    const manager = getTrimbleAuthManager()
    return manager.getStoredToken()
  } catch (error) {
    console.error('Error getting stored token:', error)
    return null
  }
}

/**
 * Logout from Trimble
 * @returns Promise that resolves when logout is complete
 */
export async function logout(): Promise<void> {
  try {
    const manager = getTrimbleAuthManager()
    await manager.logout()
  } catch (error) {
    console.error('Error logging out:', error)
    throw error
  }
}

/**
 * Fetch portfolios from Trimble API
 */
export async function fetchPortfolios(): Promise<Portfolio[]> {
  try {
    const response = await makeApiCall('/api/portfolios')
    if (response.ok) {
      return (await response.json()) as Portfolio[]
    }
    throw new Error(`Failed to fetch portfolios: ${response.status}`)
  } catch (error) {
    console.error('Error fetching portfolios:', error)
    throw error
  }
}

/**
 * Fetch projects for a specific portfolio from Trimble API
 */
export async function fetchProjects(portfolioId: string): Promise<Project[]> {
  try {
    const response = await makeApiCall(
      `/api/portfolios/${portfolioId}/projects`
    )
    if (response.ok) {
      return (await response.json()) as Project[]
    }
    throw new Error(`Failed to fetch projects: ${response.status}`)
  } catch (error) {
    console.error('Error fetching projects:', error)
    throw error
  }
}

/**
 * Fetch contracts for a specific project from Trimble API
 */
export async function fetchContracts(
  portfolioId: string,
  projectId: string
): Promise<Contract[]> {
  try {
    const response = await makeApiCall(
      `/api/portfolios/${portfolioId}/projects/${projectId}/contracts`
    )
    if (response.ok) {
      return (await response.json()) as Contract[]
    }
    throw new Error(`Failed to fetch contracts: ${response.status}`)
  } catch (error) {
    console.error('Error fetching contracts:', error)
    throw error
  }
}

/**
 * Send artifacts to ProjectSight
 *
 * Temporary using /api/checklist-to-projectsight for retro-compatibility
 * This function sends artifacts to ProjectSight using the /api/artifacts-to-projectsight endpoint.
 * It accepts the portfolio ID, project ID, contract ID, crunch UUID, dcrunch token, and download options as parameters.
 *
 * @param request The request containing information on the artifacts to be sent to ProjectSight
 * @returns Promise with the ArtifactsToProjectSightResponse containing the success status and file information
 * @throws ArtifactsToProjectSightError if the request fails
 */
export async function sendArtifactsToProjectSight(
  request: ArtifactsToProjectSightRequest
): Promise<ArtifactsToProjectSightResponse> {
  try {
    const response = await makeApiCall('/api/checklist-to-projectsight', {
      method: 'POST',
      body: JSON.stringify(request),
    })

    const data = await response.json()

    if (!response.ok) {
      throw data as ArtifactsToProjectSightError
    }

    return data as ArtifactsToProjectSightResponse
  } catch (error) {
    throw error
  }
}
